import React, { useState } from 'react';
import { Input, Button, Form } from 'reactstrap';
import FontAwesomeButton from '../../../../misc/FontAwesomeButton';
import ModalComponent from '../../../../misc/ModalComponent';
import { GridListItem, GridListHeader } from '../../../../misc/GridListComponents';
import { useTranslation } from 'react-i18next';
import { OnChangeCallback } from '../../../../../util/UtilityTypes';
import SelectInput from '../../../../misc/SelectInput';
import { Skola24EventFilterFieldOptions, Skola24EventFilterTypeOptions } from './Skola24Schedule/Skola24ScheduleConfigurationTypes';
import { CoreSkola24EventFilter, Skola24EventFilterField, Skola24EventFilterType } from '../../../../../models/CoreSkola24ScheduleConfigurationModels';


const defaultFilter: CoreSkola24EventFilter = {
    filterType: Skola24EventFilterType.None,
    filterField: Skola24EventFilterField.None,
    value: '',
}

interface Skola24FilterInputProps {
    name: string
    filters: CoreSkola24EventFilter[]
    onChange: OnChangeCallback<CoreSkola24EventFilter[]>
    disabled: boolean
}

const filterEquals = (a: CoreSkola24EventFilter, b: CoreSkola24EventFilter) => a.filterField === b.filterField && a.value === b.value && a.filterType === b.filterType;

const Skola24FilterInput = (props: Skola24FilterInputProps) => {

    const [filters, setFilters] = useState<CoreSkola24EventFilter[]>(props.filters);
    const [currentIndex, setCurrentIndex] = useState<null | number>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentFilter, setCurrentFilter] = useState<CoreSkola24EventFilter>(defaultFilter);
    const [deleteFilter, setDeleteFilter] = useState<{ filterField: number, filterType: number, value: string, index: number } | undefined>(undefined);

    const { t } = useTranslation(['common', 'configuration']);

    const submitFilter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const filter = calculateUpdatedFilters();
        if (filter) {
            setFilters(filter);
            notifyChange(filter);
        }
        setCurrentIndex(null);
        setCurrentFilter(defaultFilter);
    }

    const calculateUpdatedFilters = () => {
        if (Number.isFinite(currentIndex)) {
            return filters.map((x, i) => i === currentIndex ? currentFilter : x);
        } else if (!filters.some(x => filterEquals(x, currentFilter))) {
            return [...filters, currentFilter];
        } else {
            return null;
        }
    }

    const editFilter = (index: number) => {
        setCurrentIndex(index);
        setCurrentFilter(filters[index]);
    }

    const removeFilter = (index: number) => {
        const filterIndex = filters[index];
        setIsOpen(true);
        setDeleteFilter({ filterField: filterIndex.filterField, filterType: filterIndex.filterType, value: filterIndex.value, index: index });
    }

    const renderModal = () => {
        if (deleteFilter !== undefined) {
            return (
                <ModalComponent
                    isOpen={isOpen}
                    header={t('filterInput.remove.modalHeader', { ns: 'configuration' })}
                    disabled={props.disabled}
                    confirm={t('yes', { ns: 'common' })}
                    cancel={t('no', { ns: 'common' })}
                    toggleModal={toggleModal}
                    update={updateFilter}
                >
                    <div>
                        <p>{t(`filterInput.remove.modalConfirmationTextParam.${deleteFilter.filterField}`, { deleteFilterValue: deleteFilter.value, ns: 'configuration' })}</p>
                        <p><i>{t('filterInput.remove.modalDescriptionText', { ns: 'configuration' })} </i></p>
                    </div>
                </ModalComponent>
            )
        }
    }

    const abort = () => {
        setCurrentIndex(null);
        setCurrentFilter(defaultFilter);
    }

    const toggleModal = () => {
        setIsOpen(false);
        setDeleteFilter(undefined);
    }

    const updateFilter = () => {
        if (deleteFilter !== undefined) {
            let updatedFilters = filters.filter((_, i) => i !== deleteFilter.index)
            setFilters(updatedFilters)
            notifyChange(updatedFilters);
            toggleModal();
        }
    }

    const valid = () => {
        if (!currentFilter) {
            return false;
        }
        if (currentFilter.filterField < 1 || currentFilter.filterField> 3) {
            return false;
        }
        if (!currentFilter.value || currentFilter.value.length === 0) {
            return false;
        }
        if (currentFilter.filterType < 1 || currentFilter.filterType > 4) {
            return false;
        }

        return true;
    }

    const notifyChange = (filters: CoreSkola24EventFilter[]) => {
        props.onChange(props.name, filters)
    }

    const updateType = (name: string, value: number | undefined) => {
        if (value) {
            setCurrentFilter({ ...currentFilter, [name]: value })
        }
    };

    const updateValue: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        setCurrentFilter({ ...currentFilter, value: e.target.value })
    }

    const renderRow = (index: number | null, filter: CoreSkola24EventFilter) => {
        const editing = Number.isFinite(currentIndex);
        if (index === currentIndex) {
            return (<React.Fragment key={index}>
                <GridListItem>
                    <SelectInput
                        items={Skola24EventFilterTypeOptions.map(m => ({ value: m, text: t(`skola24Schedule.eventFilters.filterTypeOptions.options.${m}`, { ns: 'configuration' }) }))}
                        onChange={(name, value) => updateType(name, value)}
                        name='filterType'
                        selected={currentFilter.filterType}
                        defaultText={t('skola24Schedule.eventFilters.filterTypeOptions.placeholder', { ns: 'configuration' })}
                    />
                </GridListItem>
                <GridListItem>
                    <SelectInput
                        items={Skola24EventFilterFieldOptions.map(m => ({ value: m, text: t(`filterInput.options.${m}`, { ns: 'configuration' }) }))}
                        onChange={(name, value) => updateType(name, value)}
                        name='filterField'
                        selected={currentFilter.filterField}
                        defaultText={t('filterInput.options.placeholder', { ns: 'configuration' })}
                    />
                </GridListItem>
                <GridListItem>
                    <Input
                        type="text"
                        disabled={props.disabled}
                        placeholder={t('filterInput.placeHolder', { ns: 'configuration' })}
                        autoComplete="off"
                        value={currentFilter.value}
                        onChange={updateValue} />
                </GridListItem>
                {editing ? <GridListItem span={{ startColumn: 4, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled || !valid()}>{t('action.save', { ns: 'common' })}</Button>
                    <Button style={{ width: '100%' }} color="danger" onClick={() => abort()}>{t('action.cancel', { ns: 'common' })}</Button>
                </GridListItem> : <GridListItem span={{ startColumn: 4, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled || !valid()}>{t('action.add', { ns: 'common' })}</Button>
                </GridListItem>}
            </React.Fragment>)
        } else if (Number.isFinite(index)) {
            return (<React.Fragment key={index}>
                <GridListItem>{t(`skola24Schedule.eventFilters.filterTypeOptions.options.${filter.filterType}`, { ns: 'configuration' })}</GridListItem>
                <GridListItem>{t(`filterInput.options.${filter.filterField}`, { ns: 'configuration' })}</GridListItem>
                <GridListItem>{filter.value}</GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="pen" onClick={() => editFilter(Number(index))} disabled={props.disabled || editing} title={t('filterInput.edit.title', { ns: 'configuration' })} />
                </GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="trash" onClick={() => removeFilter(Number(index))} disabled={props.disabled || editing} title={t('filterInput.remove.title', { ns: 'configuration' })} />
                </GridListItem>
            </React.Fragment>);
        } else {
            return null;
        }
    }

    return (<>
        <Form className="gridList" style={{ gridTemplateColumns: '4fr 4fr 12fr 1.5fr 1.5fr' }} onSubmit={(e) => submitFilter(e)}>
            <GridListHeader>{t('skola24Schedule.eventFilters.filterType', { ns: 'configuration' })}</GridListHeader>
            <GridListHeader>{t('skola24Schedule.eventFilters.fieldType', { ns: 'configuration' })}</GridListHeader>
            <GridListHeader>{t('skola24Schedule.eventFilters.value', { ns: 'configuration' })}</GridListHeader>
            <GridListHeader span={{ startColumn: 4, columnCount: 2 }} />
            {filters.map((filter, index) => renderRow(index, filter))}
            {renderRow(null, currentFilter)}
        </Form>
        {isOpen && deleteFilter !== undefined && renderModal()}
    </>);
}

export default Skola24FilterInput;
